import { defineStore } from 'pinia';
import { ref, reactive } from 'vue';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  getLiens,
  getLien,
  createLien as createLienApi,
  updateLien as updateLienApi,
  getVendors as getVendorsApi,
  createVendor as createVendorApi,
  deleteLien as deleteLienApi,
  updateLienStatus as updateLienStatusApi,
  pushLien as pushLienApi,
  bulkPushLiens as bulkPushLiensApi,
  undoPush as undoPushApi,
  bulkUndoPush as bulkUndoPushApi,
} from '@/services/liens/LienService';
import type {
  LienData,
  LienSummaryData,
  LienPagination,
  LienUiState,
  LienStatusUpdateResponse,
  CreateLienRequest,
  UpdateLienRequest,
  CreateVendorRequest,
  VendorData,
  LienBulkResponse,
} from '@/models/lienTypes';

export const useLienStore = defineStore('lienStore', () => {
  const { handleError } = useSnackbarHandler();

  // State
  const liens = ref<LienData[]>([]);
  const count = ref<number>(0);
  const summary = ref<LienSummaryData | null>(null);
  const isSettlementLocked = ref<boolean>(false);
  const isQboConnected = ref<boolean>(false);
  const uiState: LienUiState = reactive({ loading: false, error: null });
  const pagination: LienPagination = reactive({
    offset: 0,
    limit: 25,
    orderBy: 'createDate',
    sortAscending: false,
  });

  // Actions
  async function fetchLiens(matterId: string): Promise<void> {
    uiState.loading = true;
    uiState.error = null;
    try {
      const data = await getLiens(matterId, pagination);
      liens.value = data.items ?? [];
      count.value = data.count;
      summary.value = data.summary;
      isSettlementLocked.value = data.isSettlementLocked;
      // Show the QBO column unless the backend explicitly says not connected.
      // (The list response may omit this field until the backend adds it; the
      // push endpoint itself validates QBO/trust and surfaces friendly errors.)
      isQboConnected.value = data.isQboConnected !== false;
    } catch (e: unknown) {
      uiState.error = e;
      handleError(e);
    } finally {
      uiState.loading = false;
    }
  }

  function setPage(page: number, matterId: string): void {
    pagination.offset = (page - 1) * pagination.limit;
    fetchLiens(matterId);
  }

  /** Toggle direction when re-clicking the active column; switching columns defaults to ascending. */
  function setSort(orderBy: string, matterId: string): void {
    if (pagination.orderBy === orderBy) {
      pagination.sortAscending = !pagination.sortAscending;
    } else {
      pagination.orderBy = orderBy;
      pagination.sortAscending = true;
    }
    pagination.offset = 0;
    fetchLiens(matterId);
  }

  async function createLien(
    matterId: string,
    payload: CreateLienRequest,
  ): Promise<LienData> {
    return createLienApi(matterId, payload);
  }

  async function updateLien(
    lienId: string,
    payload: UpdateLienRequest,
  ): Promise<LienData> {
    return updateLienApi(lienId, payload);
  }

  async function fetchVendors(search?: string): Promise<VendorData[]> {
    return getVendorsApi(search);
  }

  async function addVendor(
    payload: CreateVendorRequest,
  ): Promise<VendorData> {
    return createVendorApi(payload);
  }

  async function deleteLien(
    lienId: string,
  ): Promise<void> {
    await deleteLienApi(lienId);
  }

  async function fetchLienById(
    lienId: string,
  ): Promise<LienData> {
    return getLien(lienId);
  }

  async function updateLienStatus(
    lienId: string,
    status: string,
  ): Promise<LienStatusUpdateResponse> {
    const updated = await updateLienStatusApi(lienId, status);
    // Optimistic update in local state
    const idx = liens.value.findIndex((l) => l.lienId === lienId);
    if (idx !== -1) {
      liens.value[idx].status = updated.status;
      liens.value[idx].modifiedDate = updated.modifiedDate;
    }
    return updated;
  }

  // ── QBO push / undo ───────────────────────────────────────────────────────

  // A single push/undo moves the trust balance every other row's pushBlockedReason was computed
  // against, and the returned lien carries none, so reload the list (also on failure) rather than
  // patch one row. Bulk operations leave the reload to the caller, which also refreshes counts.
  async function pushLien(matterId: string, lienId: string): Promise<LienData> {
    try {
      return await pushLienApi(matterId, lienId);
    } finally {
      await fetchLiens(matterId);
    }
  }

  async function undoPush(matterId: string, lienId: string): Promise<LienData> {
    try {
      return await undoPushApi(matterId, lienId);
    } finally {
      await fetchLiens(matterId);
    }
  }

  async function bulkPushLiens(
    matterId: string,
    lienIds: string[],
  ): Promise<LienBulkResponse> {
    return bulkPushLiensApi(matterId, lienIds);
  }

  async function bulkUndoPush(
    matterId: string,
    lienIds: string[],
  ): Promise<LienBulkResponse> {
    return bulkUndoPushApi(matterId, lienIds);
  }

  function $reset(): void {
    liens.value = [];
    count.value = 0;
    summary.value = null;
    isSettlementLocked.value = false;
    isQboConnected.value = false;
    uiState.loading = false;
    uiState.error = null;
    pagination.offset = 0;
    pagination.limit = 25;
    pagination.orderBy = 'createDate';
    pagination.sortAscending = false;
  }

  return {
    liens,
    count,
    summary,
    isSettlementLocked,
    isQboConnected,
    uiState,
    pagination,
    fetchLiens,
    setPage,
    setSort,
    createLien,
    updateLien,
    fetchVendors,
    addVendor,
    deleteLien,
    updateLienStatus,
    fetchLienById,
    pushLien,
    undoPush,
    bulkPushLiens,
    bulkUndoPush,
    $reset,
  };
});
